import { tabela } from './supabase';
import { carregarFilaDoSuporte } from './gerenciaApi';
import { obterClientesComUsuarios } from './mysuiteApi';
import { normalizarTicket } from './painelGerencia';
import {
  analisarPulo, ticketsDoMesmoCliente, retratoDoTicket, clienteDoRegistro,
} from './puloFila';

// ─────────────────────────────────────────────────────────────────────────────
// Pulo de fila — a parte que fala com o MySuite e com o Supabase.
// As regras estão em puloFila.js; aqui só se busca e se grava.
// ─────────────────────────────────────────────────────────────────────────────

const TABELA = 'ticket_pulo';

/** Nome do cliente pelo código, do cadastro que o MySuite já devolve. '' sem ele. */
async function nomeDoCliente(codigo) {
  if (!codigo) return '';
  try {
    const clientes = await obterClientesComUsuarios();
    const c = clientes.find(x => Number(x.codigo) === Number(codigo));
    return String(c?.nome || '').trim();
  } catch (e) {
    return '';
  }
}

/**
 * O que a coleta de `numero` significa AGORA: os tickets pulados e os livres
 * do mesmo cliente.
 *
 * Lê a fila de pendentes FRESCA — a decisão é sobre o instante do clique, e a
 * lista da tela pode ter dois minutos. `dica` é o registro que a tela tem,
 * usado quando o ticket não está na fila de pendentes.
 *
 * Devolve `null` quando não dá para saber (a fila não respondeu): nesse caso a
 * coleta segue sem aviso — travar o atendimento porque o MySuite oscilou seria
 * pior do que deixar um pulo sem registro.
 */
export async function analisarColeta(numero, dica = null, agora = new Date()) {
  const brutos = await carregarFilaDoSuporte();
  if (!brutos) return null;

  const tickets = brutos.map(t => normalizarTicket(t)).filter(Boolean);
  const codigo = Number(numero);
  const alvo = tickets.find(t => Number(t.codigo) === codigo)
    || (dica ? normalizarTicket({ ...dica, codigo }) : null);
  if (!alvo) return null;

  const pulo = analisarPulo(tickets, alvo, agora);
  const mesmoCliente = ticketsDoMesmoCliente(tickets, alvo, agora);
  const cliente = clienteDoRegistro(alvo);

  return {
    alvo,
    posicao: pulo?.posicao || 1,
    pulados: pulo?.pulados || [],
    mesmoCliente,
    cliente,
    nomeCliente: mesmoCliente.length ? await nomeDoCliente(cliente) : '',
  };
}

/**
 * Grava o pulo. Lança em caso de falha — quem chama decide o aviso, porque a
 * essa altura o ticket JÁ foi assumido e o analista precisa saber que o
 * registro não ficou.
 */
export async function registrarPulo({ operador, analise, motivo, agora = new Date() }) {
  const { error } = await tabela(TABELA).insert({
    operador_codigo: Number(operador?.codigo) || null,
    operador_nome: operador?.nome || null,
    operador_email: operador?.email || null,
    ticket_puxado: Number(analise.alvo.codigo),
    posicao: analise.posicao,
    qtd_pulados: analise.pulados.length,
    ticket_retrato: retratoDoTicket(analise.alvo, agora),
    pulados: analise.pulados.map(t => retratoDoTicket(t, agora)),
    motivo: String(motivo || '').trim(),
  });
  if (error) throw new Error(error.message);
}

/**
 * Grava a lista do MESMO CLIENTE deixada na fila (toda ou em parte), com a
 * justificativa. Vai para a mesma tabela dos pulos, com `tipo: 'mesmo_cliente'`
 * — a gerência apura os dois no mesmo bloco. Lança em caso de falha.
 */
export async function registrarMesmoClienteIgnorado({
  operador, analise, ignorados, motivo, agora = new Date(),
}) {
  const { error } = await tabela(TABELA).insert({
    tipo: 'mesmo_cliente',
    operador_codigo: Number(operador?.codigo) || null,
    operador_nome: operador?.nome || null,
    operador_email: operador?.email || null,
    ticket_puxado: Number(analise.alvo.codigo),
    posicao: null,
    qtd_pulados: ignorados.length,
    ticket_retrato: retratoDoTicket(analise.alvo, agora),
    pulados: ignorados.map(t => retratoDoTicket(t, agora)),
    motivo: String(motivo || '').trim(),
  });
  if (error) {
    if (/tipo|posicao/i.test(error.message || '')) {
      throw new Error('a tabela ainda não aceita este registro — rode sql/alterar-tabela_ticket-pulo-mesmo-cliente_20261029_1630.sql');
    }
    throw new Error(error.message);
  }
}

/** Pulos desde uma data, do mais recente ao mais antigo. `null` quando falha. */
export async function listarPulos(desde) {
  try {
    const { data, error } = await tabela(TABELA)
      .select('*')
      .gte('criado_em', desde.toISOString())
      .order('criado_em', { ascending: false })
      .limit(500);
    if (error) throw new Error(error.message);
    return data || [];
  } catch (err) {
    console.warn('[Orion] Pulos de fila:', err.message);
    return null;
  }
}

/**
 * Apura o pulo: o gerente decide se ele CONTA (`contabiliza: true`) ou não
 * (`false`) nas apurações. Lança em caso de falha.
 *
 * Também serve para rever uma decisão: apurar de novo troca a decisão, quem
 * apurou e a hora — o parecer anterior só é trocado se vier um novo.
 */
export async function apurarPulo(id, { gerente, parecer, contabiliza }) {
  const campos = {
    apurado_em: new Date().toISOString(),
    apurado_por: gerente || null,
    contabiliza: !!contabiliza,
  };
  const texto = String(parecer || '').trim();
  if (texto) campos.parecer = texto;

  const { error } = await tabela(TABELA).update(campos).eq('id', id);
  if (error) {
    // Sem o SQL da coluna, o PostgREST recusa o campo desconhecido.
    if (/contabiliza/i.test(error.message || '')) {
      throw new Error('a coluna "contabiliza" ainda não existe — rode sql/alterar-tabela_ticket-pulo-contabiliza_20261029_1500.sql');
    }
    throw new Error(error.message);
  }
}
